import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { tenantApi } from '../../api/endpoints/tenant';
import { platformApi } from '../../api/endpoints/platform';
import { catalogApi } from '../../api/endpoints/catalog';
import { resolvePlatformContainerTenantId } from '../../lib/brandingTenant';
import { Badge } from '../ui/badge';

export function MyRoleAssignments() {
    const { roleAssignments, user } = useAuth();
    // El platform admin tiene user.tenantId === '*' (wildcard global). Sus roles NO son "shared roles"
    // de un tenant: viven a nivel plataforma (tabla PlatformRole_GT) y se sirven por GET /platform/roles.
    // Pegarle a /tenants/platform/roles resolvía la tabla shared inexistente PlatformSharedRole_GT →
    // ResourceNotFoundException → 500. Por eso el platform admin va al endpoint platform; el resto, al de su tenant.
    const isPlatform = user?.tenantId === '*';
    const tid = resolvePlatformContainerTenantId(user?.tenantId);

    // Un mismo rol puede estar asignado en varios scopes; sin mostrar scope, deduplicamos por roleId.
    const uniqueRoles = Array.from(new Map(roleAssignments.map((a) => [a.roleId, a])).values());

    // Permisos por rol (definición del rol). Si el user no puede listar roles (403), degrada:
    // muestra los roles igual, solo sin el detalle de permisos. Ambas ramas devuelven el mismo shape
    // mínimo ({ roleId, roleName, permissions }) que consume el componente.
    const rolesQ = useQuery({
        queryKey: isPlatform ? ['platform', 'roles'] : ['tenant', tid, 'roles'],
        queryFn: async () => {
            if (isPlatform) {
                const res = await platformApi.listPlatformRoles();
                // El list platform expone `displayName` (no `roleName`); lo mapeamos al shape del componente.
                return {
                    items: res.items.map((r) => ({
                        roleId: r.roleId,
                        roleName: r.displayName,
                        permissions: r.permissions ?? [],
                    })),
                };
            }
            const res = await tenantApi.listRoles(tid ?? '');
            return {
                items: res.items.map((r) => ({
                    roleId: r.roleId,
                    roleName: r.roleName,
                    permissions: r.permissions,
                })),
            };
        },
        enabled: isPlatform || !!tid,
    });
    const catalogQ = useQuery({
        queryKey: ['catalog', 'permissions'],
        queryFn: () => catalogApi.listPermissions(),
    });

    const permsByRole = new Map((rolesQ.data?.items ?? []).map((r) => [r.roleId, r.permissions]));
    // El roleName de la asignación (/me/profile) a veces viene vacío → fallback a la
    // definición del rol (listRoles), y en último caso al roleId.
    const nameByRole = new Map((rolesQ.data?.items ?? []).map((r) => [r.roleId, r.roleName]));
    // key del permiso → título legible del catálogo (displayName; fallback a description, luego al key).
    const labelOf = new Map(
        (catalogQ.data?.items ?? []).map((p) => [p.key, p.displayName || p.description || p.key]),
    );

    const [open, setOpen] = useState<Set<string>>(new Set());
    const toggle = (roleId: string): void => {
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(roleId)) next.delete(roleId);
            else next.add(roleId);
            return next;
        });
    };

    if (uniqueRoles.length === 0)
        return <p className="text-muted-foreground">Sin roles asignados.</p>;

    return (
        <div className="space-y-2">
            {uniqueRoles.map((a) => {
                const perms = permsByRole.get(a.roleId) ?? [];
                const canExpand = perms.length > 0;
                const isOpen = open.has(a.roleId);
                return (
                    <div key={a.roleId} className="rounded-lg border border-border">
                        <button
                            type="button"
                            disabled={!canExpand}
                            onClick={() => {
                                toggle(a.roleId);
                            }}
                            className="flex w-full items-center gap-2 p-3 text-left disabled:cursor-default"
                        >
                            <ShieldCheck className="size-4 shrink-0 text-brand-accent" />
                            <span className="font-medium">
                                {/* Nombre del rol: de la asignación, o de la definición (listRoles).
                                    NUNCA el roleId crudo — si no hay nombre, texto legible. */}
                                {a.roleName || nameByRole.get(a.roleId) || 'Rol sin nombre'}
                            </span>
                            {canExpand && (
                                <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                                    {perms.length} {perms.length === 1 ? 'permiso' : 'permisos'}
                                    {isOpen ? (
                                        <ChevronDown className="size-4" />
                                    ) : (
                                        <ChevronRight className="size-4" />
                                    )}
                                </span>
                            )}
                        </button>
                        {canExpand && isOpen && (
                            <div className="flex flex-wrap gap-1.5 border-t border-border p-3">
                                {perms.map((p) => (
                                    <Badge key={p} variant="secondary" className="font-normal">
                                        {labelOf.get(p) ?? p}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
